/**
 * NotificationDetailDialog Component
 * Professional modal dialog for viewing notification details
 */

import React, { useEffect, useRef } from 'react';
import { formatNotificationTime, getNotificationTypeConfig } from '../../services/notificationService';

/**
 * Get icon SVG based on notification type
 */
const NotificationIcon = ({ type }) => {
  const typeConfig = getNotificationTypeConfig(type);
  
  const iconPaths = {
    'calendar-plus': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    ),
    'calendar-x': (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l4 4m0-4l-4 4" />
      </>
    ),
    'calendar-check': (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l2 2 4-4" />
      </>
    ),
    'bell': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    ),
    'heart-pulse': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    ),
    'activity': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    ),
    'wind': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h8a4 4 0 110 8H3m6-8h4a4 4 0 110 8h-4" />
    ),
    'file-text': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    'bar-chart': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    ),
    'alert-triangle': (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    )
  };

  return (
    <svg 
      className={`w-6 h-6 ${typeConfig.textColor}`}
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      {iconPaths[typeConfig.icon] || iconPaths['bell']}
    </svg>
  );
};

/**
 * Metadata display for different notification types
 */
const MetadataDisplay = ({ notification }) => {
  const { metadata, notification_type } = notification;
  
  if (!metadata || Object.keys(metadata).length === 0) {
    return null;
  }

  // Daily bookings report - special display
  if (notification_type === 'daily_bookings_report') {
    return (
      <div className="mt-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{metadata.total_bookings || 0}</p>
            <p className="text-xs text-gray-600">Total</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{metadata.completed || 0}</p>
            <p className="text-xs text-gray-600">Completed</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-600">{metadata.cancelled || 0}</p>
            <p className="text-xs text-gray-600">Cancelled</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-600">{metadata.pending || 0}</p>
            <p className="text-xs text-gray-600">Pending</p>
          </div>
        </div>

        {/* By Type */}
        {metadata.by_type && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">By Type</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-lg font-semibold">{metadata.by_type.standard_telehealth || 0}</p>
                <p className="text-xs text-gray-500">Standard</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-lg font-semibold">{metadata.by_type.clinical_telehealth || 0}</p>
                <p className="text-xs text-gray-500">Clinical</p>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <p className="text-lg font-semibold">{metadata.by_type.home_care || 0}</p>
                <p className="text-xs text-gray-500">Home Care</p>
              </div>
            </div>
          </div>
        )}

        {/* Top Doctors & Clinics */}
        <div className="grid grid-cols-2 gap-4">
          {metadata.top_doctors && metadata.top_doctors.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Top Doctors</h4>
              <ul className="space-y-1">
                {metadata.top_doctors.map((doc, idx) => (
                  <li key={idx} className="text-sm flex justify-between">
                    <span className="text-gray-600">{doc.name}</span>
                    <span className="font-medium">{doc.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {metadata.top_clinics && metadata.top_clinics.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Top Clinics</h4>
              <ul className="space-y-1">
                {metadata.top_clinics.map((clinic, idx) => (
                  <li key={idx} className="text-sm flex justify-between">
                    <span className="text-gray-600">{clinic.name}</span>
                    <span className="font-medium">{clinic.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bookings List */}
        {metadata.bookings && metadata.bookings.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Bookings ({metadata.bookings.length})</h4>
            <div className="max-h-48 overflow-y-auto border rounded">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left text-gray-600">Patient</th>
                    <th className="px-2 py-1 text-left text-gray-600">Doctor</th>
                    <th className="px-2 py-1 text-left text-gray-600">Clinic</th>
                    <th className="px-2 py-1 text-left text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {metadata.bookings.map((booking, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-2 py-1">{booking.patient_name}</td>
                      <td className="px-2 py-1">{booking.doctor_name}</td>
                      <td className="px-2 py-1">{booking.clinic_name}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          booking.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Booking notifications
  if (notification_type.startsWith('booking_')) {
    return (
      <div className="mt-4 bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Appointment Details</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {metadata.patient_name && (
            <div>
              <span className="text-gray-500">Patient:</span>
              <p className="font-medium">{metadata.patient_name}</p>
            </div>
          )}
          {metadata.doctor_name && (
            <div>
              <span className="text-gray-500">Doctor:</span>
              <p className="font-medium">{metadata.doctor_name}</p>
            </div>
          )}
          {metadata.clinic_name && (
            <div>
              <span className="text-gray-500">Clinic:</span>
              <p className="font-medium">{metadata.clinic_name}</p>
            </div>
          )}
          {metadata.booking_date && (
            <div>
              <span className="text-gray-500">Date:</span>
              <p className="font-medium">{metadata.booking_date}</p>
            </div>
          )}
          {metadata.booking_time && (
            <div>
              <span className="text-gray-500">Time:</span>
              <p className="font-medium">{metadata.booking_time}</p>
            </div>
          )}
          {metadata.booking_type && (
            <div>
              <span className="text-gray-500">Type:</span>
              <p className="font-medium capitalize">{metadata.booking_type.replace('_', ' ')}</p>
            </div>
          )}
          {metadata.reason && (
            <div className="col-span-2">
              <span className="text-gray-500">Reason:</span>
              <p className="font-medium">{metadata.reason}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vitals/ECG/Spirometry notifications
  if (['vitals_uploaded', 'ecg_result_uploaded', 'spirometry_uploaded'].includes(notification_type)) {
    return (
      <div className="mt-4 bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Details</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {metadata.patient_name && (
            <div>
              <span className="text-gray-500">Patient:</span>
              <p className="font-medium">{metadata.patient_name}</p>
            </div>
          )}
          {metadata.vital_types && metadata.vital_types.length > 0 && (
            <div className="col-span-2">
              <span className="text-gray-500">Vitals Recorded:</span>
              <p className="font-medium">{metadata.vital_types.join(', ')}</p>
            </div>
          )}
          {metadata.workstation_name && (
            <div>
              <span className="text-gray-500">Workstation:</span>
              <p className="font-medium">{metadata.workstation_name}</p>
            </div>
          )}
          {(metadata.recorded_at || metadata.uploaded_at) && (
            <div>
              <span className="text-gray-500">Recorded:</span>
              <p className="font-medium">
                {new Date(metadata.recorded_at || metadata.uploaded_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Generic metadata display
  return (
    <div className="mt-4 bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Information</h4>
      <pre className="text-xs text-gray-600 overflow-auto max-h-32">
        {JSON.stringify(metadata, null, 2)}
      </pre>
    </div>
  );
};

/**
 * NotificationDetailDialog Component
 */
const NotificationDetailDialog = ({ 
  notification, 
  onClose, 
  onMarkRead,
  onDelete 
}) => {
  // Track which notification IDs we've already marked as read to prevent double-calls
  const markedReadRef = useRef(new Set());

  // Mark as read when dialog opens
  useEffect(() => {
    if (notification && !notification.is_read && onMarkRead) {
      // Only mark as read if we haven't already processed this notification
      if (!markedReadRef.current.has(notification.id)) {
        markedReadRef.current.add(notification.id);
        console.log(`📢 Marking notification ${notification.id} as read`);
        onMarkRead(notification.id);
      }
    }
  }, [notification, onMarkRead]);

  // Clear the set when dialog closes (notification becomes null)
  useEffect(() => {
    if (!notification) {
      markedReadRef.current.clear();
    }
  }, [notification]);

  if (!notification) return null;

  const typeConfig = getNotificationTypeConfig(notification.notification_type);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden transform transition-all">
          {/* Header */}
          <div className={`${typeConfig.bgColor} px-6 py-4`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 p-2 bg-white rounded-lg shadow-sm">
                  <NotificationIcon type={notification.notification_type} />
                </div>
                <div>
                  <span className={`text-xs font-medium ${typeConfig.textColor}`}>
                    {typeConfig.label}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-0.5">
                    {notification.title}
                  </h3>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            <p className="text-gray-700">{notification.message}</p>
            
            {/* Timestamp */}
            <p className="text-sm text-gray-400 mt-2">
              {formatNotificationTime(notification.created_at)} 
              {notification.is_read && notification.read_at && (
                <span className="ml-2">
                  - Read {formatNotificationTime(notification.read_at)}
                </span>
              )}
            </p>
            
            {/* Metadata */}
            <MetadataDisplay notification={notification} />
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3">
            {onDelete && (
              <button
                onClick={() => {
                  onDelete(notification.id);
                  onClose();
                }}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailDialog;
